import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  fecha,
  centro,
  totalizar,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
  construirLibroBlob,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte de Conciliacion Facturas - Complementos de Pago.
 *
 * `construirTablaFacturas` y `construirTablaComplementos` corren una sola vez y
 * alimentan los dos archivos, para que no puedan decir cosas distintas.
 */

export const ETIQUETAS_ESTATUS = {
  noAplica: "No aplica",
  sinCobros: "Sin cobros",
  faltaComplemento: "Falta complemento",
  sinTimbrar: "Complemento sin timbrar",
  conciliada: "Conciliada",
  diferencia: "Diferencia",
  pueConComplemento: "PUE con complemento",
  sobreCancelada: "Complemento sobre cancelada",
};

// Colores del Tag en pantalla: verde = cuadra, rojo = hallazgo duro,
// naranja = revisar, morado/volcan = anomalia, gris = no aplica.
export const COLOR_ESTATUS = {
  noAplica: "default",
  sinCobros: "blue",
  faltaComplemento: "red",
  sinTimbrar: "red",
  conciliada: "green",
  diferencia: "orange",
  pueConComplemento: "purple",
  sobreCancelada: "volcano",
};

export const ESTADO_FACTURA = {
  0: "Cancelada",
  1: "Sin entregar",
  2: "Entregada parcial",
  3: "Entregada",
};

const COLUMNAS_FACTURAS = [
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 52 },
  { titulo: "UUID", tipo: "texto", ancho: 38, enPdf: false },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 46 },
  { titulo: "Cliente", tipo: "texto", ancho: 34, anchoPdf: 92 },
  { titulo: "RFC", tipo: "texto", ancho: 16, anchoPdf: 62 },
  { titulo: "Metodo", tipo: "centro", ancho: 10, anchoPdf: 30 },
  { titulo: "Forma de pago", tipo: "texto", ancho: 24, enPdf: false },
  { titulo: "Subtotal", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Impuestos", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Total", tipo: "moneda", ancho: 15 },
  { titulo: "Cobrado", tipo: "moneda", ancho: 15 },
  { titulo: "Pendiente", tipo: "moneda", ancho: 15 },
  { titulo: "Amparado", tipo: "moneda", ancho: 15 },
  { titulo: "Diferencia", tipo: "moneda", ancho: 15 },
  { titulo: "Complementos timbrados", tipo: "numero", ancho: 13, anchoPdf: 34 },
  { titulo: "Sin timbrar", tipo: "numero", ancho: 11, anchoPdf: 34 },
  // Los UUID que amparan la factura, que es lo que se coteja contra el SAT. En el
  // PDF no cabe (36 caracteres por complemento): ahi van uno por renglon en la
  // seccion de Complementos.
  { titulo: "UUID complementos", tipo: "texto", ancho: 42, enPdf: false },
  { titulo: "Estatus", tipo: "texto", ancho: 24, anchoPdf: 76 },
  { titulo: "Estado", tipo: "texto", ancho: 16, enPdf: false },
];

const COLUMNAS_COMPLEMENTOS = [
  { titulo: "Folio factura", tipo: "texto", ancho: 16, anchoPdf: 54 },
  { titulo: "UUID factura", tipo: "texto", ancho: 38, enPdf: false },
  { titulo: "Cliente", tipo: "texto", ancho: 34, anchoPdf: 96 },
  { titulo: "RFC", tipo: "texto", ancho: 16, enPdf: false },
  { titulo: "Folio complemento", tipo: "texto", ancho: 18, anchoPdf: 52 },
  // Esta si va al PDF: es la columna que se coteja contra el SAT.
  { titulo: "UUID complemento", tipo: "texto", ancho: 40, anchoPdf: 150 },
  { titulo: "Timbrado", tipo: "texto", ancho: 11, anchoPdf: 42 },
  { titulo: "Fecha complemento", tipo: "fecha", ancho: 14, enPdf: false },
  { titulo: "Fecha pago", tipo: "fecha", ancho: 12, anchoPdf: 48 },
  { titulo: "Forma de pago", tipo: "texto", ancho: 24, anchoPdf: 70 },
  { titulo: "Referencia", tipo: "texto", ancho: 18, enPdf: false },
  { titulo: "Parcialidad", tipo: "numero", ancho: 11, anchoPdf: 32 },
  { titulo: "Saldo anterior", tipo: "moneda", ancho: 15 },
  { titulo: "Importe pagado", tipo: "moneda", ancho: 15 },
  { titulo: "Saldo insoluto", tipo: "moneda", ancho: 15 },
  { titulo: "Estado", tipo: "texto", ancho: 12, anchoPdf: 44 },
];

/** "03 - Transferencia electronica de fondos" a partir del id de la forma de pago. */
export const etiquetaFormaDePago = (formasDePago, formaDePagoId) => {
  if (!formaDePagoId || !formasDePago) return "";
  const forma = formasDePago.get(formaDePagoId.toString());
  if (!forma) return "";
  return [forma.c_FormaPago, forma.descripcion].filter(Boolean).join(" - ");
};

export const impuestosDe = (registro) =>
  Math.round(((registro.total || 0) - (registro.subtotal || 0)) * 100) / 100;

export const construirTablaFacturas = (registros, { formasDePago } = {}) => {
  const filas = (registros || []).map((registro) => [
    texto(registro.folio),
    texto(registro.uuid),
    fecha(registro.fechaFactura),
    texto(registro.cliente),
    texto(registro.rfc),
    centro(registro.metodoDePago),
    texto(etiquetaFormaDePago(formasDePago, registro.formaDePagoId)),
    moneda(registro.subtotal),
    moneda(impuestosDe(registro)),
    moneda(registro.total),
    moneda(registro.totalPagado),
    moneda(registro.pendiente),
    moneda(registro.amparado),
    moneda(registro.diferencia),
    numero(registro.complementosTimbrados || 0),
    numero(registro.complementosSinTimbrar || 0),
    texto((registro.uuidsComplementos || []).join(" | ")),
    texto(registro.estatusEtiqueta || ETIQUETAS_ESTATUS[registro.estatus] || ""),
    texto(ESTADO_FACTURA[registro.estadoFactura] || ""),
  ]);

  return { columnas: COLUMNAS_FACTURAS, filas, totales: totalizar(COLUMNAS_FACTURAS, filas) };
};

export const construirTablaComplementos = (registros, { formasDePago } = {}) => {
  const filas = [];
  for (const registro of registros || []) {
    for (const complemento of registro.complementos || []) {
      filas.push([
        texto(registro.folio),
        texto(registro.uuid),
        texto(registro.cliente),
        texto(registro.rfc),
        texto(complemento.folio),
        // Vacio = el Pago existe con `complemento: true` pero nunca se timbro.
        texto(complemento.uuid || "SIN UUID"),
        texto(complemento.timbrado === false ? "No" : "Si"),
        fecha(complemento.fechaComplemento),
        fecha(complemento.fechaPago),
        texto(etiquetaFormaDePago(formasDePago, complemento.formaDePagoId)),
        texto(complemento.referencia),
        numero(complemento.parcialidad),
        moneda(complemento.saldoAnterior),
        moneda(complemento.impPagado),
        moneda(complemento.saldoInsoluto),
        texto(complemento.estado === 0 ? "Cancelado" : "Vigente"),
      ]);
    }
  }

  return {
    columnas: COLUMNAS_COMPLEMENTOS,
    filas,
    totales: totalizar(COLUMNAS_COMPLEMENTOS, filas),
  };
};

const TITULO = "Conciliacion Facturas - Complementos de Pago";

export const nombreArchivoConciliacion = (empresaNombre) =>
  `Conciliacion_Facturas_Pagos_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const nombreArchivo = nombreArchivoConciliacion;

const hojasConciliacion = ({ registros, empresaNombre, descripcionFiltros, formasDePago }) => {
  const ctx = { formasDePago };
  const encabezado = { empresaNombre, descripcionFiltros };
  return [
    {
      nombre: "Facturas",
      hoja: construirHojaTabla(construirTablaFacturas(registros, ctx), {
        ...encabezado,
        titulo: TITULO,
      }),
    },
    {
      nombre: "Complementos",
      hoja: construirHojaTabla(construirTablaComplementos(registros, ctx), {
        ...encabezado,
        titulo: "Complementos de pago por factura",
      }),
    },
  ];
};

export const descargarExcel = (params) => {
  descargarLibro(hojasConciliacion(params), nombreArchivo(params.empresaNombre));
};

/** El mismo Excel como Blob, para que la descarga masiva lo meta al ZIP. */
export const excelConciliacionBlob = (params) => construirLibroBlob(hojasConciliacion(params));

export const descargarPdf = ({
  registros, empresaNombre, descripcionFiltros, totales = {}, formasDePago,
}) => {
  const ctx = { formasDePago };
  const tablaFacturas = construirTablaFacturas(registros, ctx);
  const tablaComplementos = construirTablaComplementos(registros, ctx);

  // Las cifras de auditoria sólo existen tras la corrida completa; si el reporte
  // se pidió paginado se marcan con un guion en vez de inventar un cero.
  const auditoria = (valor) => (totales.completo ? String(valor || 0) : "-");

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: TITULO,
        empresaNombre,
        descripcionFiltros,
        indicadores: [
          { etiqueta: "Facturas", valor: String(totales.facturas || 0) },
          { etiqueta: "PPD", valor: String(totales.facturasPpd || 0) },
          { etiqueta: "Complementos timbrados", valor: auditoria(totales.complementos) },
          { etiqueta: "Falta complemento", valor: auditoria(totales.sinComplemento) },
          { etiqueta: "Sin timbrar", valor: auditoria(totales.sinTimbrar) },
          { etiqueta: "Con diferencia", valor: auditoria(totales.conDiferencia) },
        ],
        contador: tablaFacturas.filas.length,
        secciones: [
          { titulo: "Facturas", ...paraPdf(tablaFacturas) },
          { titulo: "Complementos de pago", ...paraPdf(tablaComplementos) },
        ],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
